import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCharbeePlanId, useSelector } from '../../../hooks/usePrerenderUser';
import { HubSpot } from '~features/CustomerSupport';
import { useEvent } from '~features/events/hooks/useEvent';
import { useTrial } from '~features/FreeTrial/useTrial';
import { useSubscriptionState } from '../PlanStatus';
import useTrackBillingEvent from './useTrackBillingEvent';

export type ReactivationLocation = 'global_banner' | 'search_performance_card';

/**
 * The reactivate-plan entry point shared by every "your plan ended" surface.
 * Callers render `SubscriptionReactivationModal` themselves with `modalProps`,
 * so each surface decides whether it stays mounted while the modal is open.
 */
export function useReactivatePlan(location: ReactivationLocation) {
  const [isModalOpen, setModalOpen] = useState(false);
  const navigate = useNavigate();
  const chargebeePlanId = useCharbeePlanId();
  const planName = useSelector((state) => state.prerenderUser.planName);
  const [, paymentMethodState, subscriptionState] = useSubscriptionState();
  const { discountAvailable } = useTrial();
  const { track } = useEvent();
  const trackBillingEvent = useTrackBillingEvent();

  const startReactivation = () => {
    trackBillingEvent('AccountReactivationStarted', {
      subscription_plan: planName,
      overdue_amount: subscriptionState.totalDues,
      location,
    });

    if (subscriptionState.hasHierarchies) {
      HubSpot.openHubSpotForm();
      return;
    }

    if (!paymentMethodState.isChargeable) {
      navigate(`/billing/plans/?selectedPlan=${chargebeePlanId}`);
    } else {
      setModalOpen(true);
    }
  };

  const afterReactivate = () => {
    track('Account Reactivated', {
      subscription_plan: planName,
      overdue_amount: subscriptionState.totalDues,
    });
  };

  return {
    startReactivation,
    isModalOpen,
    modalProps: {
      open: isModalOpen,
      onCancel: () => setModalOpen(false),
      afterReactivate,
      planName,
      discountAvailable,
    },
  };
}
